import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getScoreColor } from '../../theme';

const formatPercent = (value) => {
  if (value === null || value === undefined) return 'N/A';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

const SectorsTable = ({ sortedScores, currentLang }) => {
  const { t } = useTranslation();

  return (
    <Box sx={{
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2.5,
      overflow: 'hidden',
    }}>
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
          {t('sectorMomentum.table.title')}
        </Typography>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {[
                { label: '#', minW: 40, align: 'center' },
                { label: t('sectorMomentum.table.sector'), minW: 130 },
                { label: t('sectorMomentum.table.score'), minW: 80, align: 'center' },
                { label: t('sectorMomentum.table.macro'), minW: 70, align: 'center' },
                { label: t('sectorMomentum.table.returns'), minW: 100, align: 'center' },
                { label: t('sectorMomentum.table.drivers'), minW: 200 },
              ].map((col, i) => (
                <TableCell
                  key={i}
                  align={col.align || 'left'}
                  sx={{
                    fontWeight: 700, fontSize: '0.72rem', py: 1, px: 1.25,
                    minWidth: col.minW, whiteSpace: 'nowrap', bgcolor: 'background.paper',
                    borderBottom: '2px solid', borderColor: 'divider',
                    textTransform: 'uppercase', letterSpacing: 0.3, color: 'text.secondary',
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedScores.map((sector, index) => {
              const primaryScore = sector.final_score ?? 0;
              const scoreColor = getScoreColor(sector.final_score_level);
              
              return (
                <TableRow
                  key={sector.sector_code}
                  hover
                  sx={{ '&:last-child td': { borderBottom: 0 } }}
                >
                  {/* Rank */}
                  <TableCell align="center" sx={{ py: 1.25, px: 1 }}>
                    <Typography variant="body2" sx={{
                      fontWeight: 700, fontSize: '0.78rem', fontFeatureSettings: '"tnum"',
                      color: index < 3 ? '#d97706' : 'text.secondary',
                    }}>
                      {index + 1}
                    </Typography>
                  </TableCell>

                  {/* Sector Name */}
                  <TableCell sx={{ py: 1.25, px: 1.25 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                      {sector.sector_name
                        ? (currentLang === 'vi' ? sector.sector_name.vi : sector.sector_name.en)
                        : sector.sector_code}
                    </Typography>
                  </TableCell>

                  {/* Combined Score */}
                  <TableCell align="center" sx={{ py: 1.25, px: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center' }}>
                      <Typography variant="body2" sx={{
                        color: scoreColor, fontWeight: 700, fontSize: '0.85rem',
                        fontFeatureSettings: '"tnum"',
                      }}>
                        {primaryScore.toFixed(1)}
                      </Typography>
                      <Box sx={{
                        width: 32, height: 4, borderRadius: 2, bgcolor: `${scoreColor}18`,
                      }}>
                        <Box sx={{
                          width: `${Math.min(primaryScore, 100)}%`, height: '100%',
                          bgcolor: scoreColor, borderRadius: 2,
                        }} />
                      </Box>
                    </Box>
                  </TableCell>

                  {/* Macro Impulse */}
                  <TableCell align="center" sx={{ py: 1.25, px: 1 }}>
                    <Typography variant="body2" sx={{
                      fontWeight: 600, fontSize: '0.78rem', fontFeatureSettings: '"tnum"',
                      color: 'text.secondary',
                    }}>
                      {sector.impulse_score_0_100.toFixed(1)}
                    </Typography>
                  </TableCell>

                  {/* Returns 1d / 5d */}
                  <TableCell align="center" sx={{ py: 1.25, px: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      {[
                        { val: sector.price_momentum?.ret_1d_pct, label: '1d' },
                        { val: sector.price_momentum?.ret_5d_pct, label: '5d' },
                      ].map(({ val, label }) => val != null ? (
                        <Typography key={label} variant="caption" sx={{
                          fontWeight: 600, fontSize: '0.72rem', fontFeatureSettings: '"tnum"',
                          color: val >= 0 ? '#059669' : '#dc2626',
                        }}>
                          {formatPercent(val)}
                        </Typography>
                      ) : null)}
                    </Box>
                  </TableCell>

                  {/* Top Drivers */}
                  <TableCell sx={{ py: 1.25, px: 1 }}>
                    {sector.top_drivers && sector.top_drivers.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {sector.top_drivers.map((driver, idx) => (
                          <Tooltip
                            key={idx}
                            title={`${driver.indicator_name || driver.indicator}: ${driver.contribution?.toFixed(3) || 'N/A'}`}
                            arrow
                          >
                            <Chip
                              label={driver.indicator_name || driver.indicator}
                              size="small"
                              icon={driver.direction === 'TAILWIND'
                                ? <TrendingUp sx={{ fontSize: 12 }} />
                                : driver.direction === 'HEADWIND'
                                ? <TrendingDown sx={{ fontSize: 12 }} />
                                : undefined}
                              sx={{
                                height: 22, fontSize: '0.68rem', fontWeight: 600,
                                bgcolor: driver.direction === 'TAILWIND' ? '#ecfdf5'
                                       : driver.direction === 'HEADWIND' ? '#fef2f2' : '#f3f4f6',
                                color: driver.direction === 'TAILWIND' ? '#059669'
                                     : driver.direction === 'HEADWIND' ? '#dc2626' : '#6b7280',
                                border: 'none', borderRadius: 1,
                                '& .MuiChip-icon': { ml: 0.5 },
                              }}
                            />
                          </Tooltip>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SectorsTable;
